import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Menu, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { LiveIndicator } from "../components/LiveIndicator";
import useUnreadChats from "../hooks/useUnreadChats";
import { ChatBadge } from "../components/ChatBadge";

/**
 * Universal module shell with sidebar (desktop) + bottom-nav (mobile).
 *
 * v6.7.4 — Sidebar desktop colapsável.
 *   - Botão na base alterna entre w-64 (expandido) ↔ w-16 (recolhido)
 *   - Recolhido = ícones-only, label visível no hover (tooltip nativo via title)
 *   - Preferência persistida por módulo em localStorage (`sidebar_collapsed_{key}`)
 */
export default function ModuleLayout({ config, user, logout, children }) {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { total: unreadTotal } = useUnreadChats();
  const t = config.theme;
  const storageKey = `sidebar_collapsed_${config.key}`;

  // Carrega preferência ao montar
  useEffect(() => {
    try {
      setCollapsed(localStorage.getItem(storageKey) === "true");
    } catch {
      /* ignore */
    }
  }, [storageKey]);

  const toggleCollapsed = () => {
    setCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem(storageKey, String(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className={`min-h-screen ${t.bg} ${t.isDarkMode ? "text-zinc-100" : ""}`} data-testid={`module-layout-${config.key}`}>
      {/* Mobile top bar */}
      <header className={`md:hidden ${t.headerBg} border-b ${t.isDarkMode ? "border-zinc-800" : ""} sticky top-0 z-30`}>
        <div className="px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setMobileSidebarOpen(true)}
            className={`p-2 rounded-lg ${t.isDarkMode ? "hover:bg-zinc-800" : "hover:bg-zinc-100"}`}
            data-testid="mobile-menu-toggle"
          >
            <Menu className="w-7 h-7" strokeWidth={2.5} />
          </button>
          <div className="flex items-center gap-2">
            <span className={`font-bold text-lg ${t.brand}`}>{config.name}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${t.isDarkMode ? "bg-zinc-800" : "bg-zinc-100"}`}>
              {config.role}
            </span>
          </div>
          <button onClick={handleLogout} className={`p-2 rounded-lg ${t.isDarkMode ? "hover:bg-zinc-800" : "hover:bg-zinc-100"}`} data-testid="mobile-logout">
            <LogOut className="w-7 h-7" strokeWidth={2.5} />
          </button>
        </div>
      </header>

      <div className="flex">
        {/* Desktop sidebar — colapsável */}
        <aside
          className={`hidden md:flex md:flex-col ${collapsed ? "w-16" : "w-64"} ${t.sidebar} ${t.sidebarText || "text-zinc-900"} h-screen sticky top-0 transition-[width] duration-200 ease-out`}
          data-testid={`sidebar-${config.key}`}
          data-collapsed={collapsed ? "true" : "false"}
        >
          {/* Brand */}
          <div className={`${collapsed ? "p-3" : "p-6"} border-b ${t.isDarkSidebar ? "border-white/10" : "border-zinc-200"} flex items-center ${collapsed ? "justify-center" : "gap-3"}`}>
            <div className={`w-10 h-10 rounded-xl ${t.accent} flex items-center justify-center text-white font-bold text-lg flex-shrink-0`}>
              G
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <p className="font-bold text-lg truncate">{config.name}</p>
                <p className={`text-xs truncate ${t.isDarkSidebar ? "text-white/60" : "text-zinc-500"}`}>{config.role}</p>
              </div>
            )}
          </div>

          {/* User info — esconde em collapsed */}
          {user && !collapsed && (
            <div className={`px-6 py-4 border-b ${t.isDarkSidebar ? "border-white/10" : "border-zinc-200"}`}>
              <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user.name}</p>
              <p className={`text-xs truncate mb-2 ${t.isDarkSidebar ? "text-white/50" : "text-zinc-500"}`}>{user.email}</p>
              <LiveIndicator compact />
            </div>
          )}

          {/* Nav items */}
          <nav className={`flex-1 ${collapsed ? "px-2" : "px-3"} py-4 space-y-1 overflow-y-auto`}>
            {config.nav.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.to);
              const baseClasses = active
                ? t.isDarkSidebar
                  ? "bg-white/10 font-semibold"
                  : "bg-zinc-100 font-semibold"
                : t.isDarkSidebar
                ? "hover:bg-white/5"
                : "hover:bg-zinc-100";
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`sidebar-${item.testId}`}
                  title={collapsed ? item.label : undefined}
                  className={`flex items-center ${collapsed ? "justify-center" : "gap-3"} px-3 py-2.5 rounded-lg transition-colors relative ${baseClasses}`}
                >
                  <Icon className="w-7 h-7 shrink-0" strokeWidth={2.4} />
                  {!collapsed && <span className="text-sm truncate flex-1">{item.label}</span>}
                  {item.chatBadge && unreadTotal > 0 && (
                    <ChatBadge count={unreadTotal} className={collapsed ? "absolute top-0.5 right-0.5" : ""} testId={`${item.testId}-chat-badge`} />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Toggle + Logout */}
          <div className={`${collapsed ? "p-2" : "p-3"} border-t ${t.isDarkSidebar ? "border-white/10" : "border-zinc-200"} space-y-1`}>
            <button
              onClick={toggleCollapsed}
              className={`w-full flex items-center ${collapsed ? "justify-center" : "gap-3"} px-3 py-2 rounded-lg text-sm transition-colors ${
                t.isDarkSidebar ? "text-white/80 hover:bg-white/10" : "text-zinc-600 hover:bg-zinc-100"
              }`}
              data-testid="sidebar-collapse-toggle"
              title={collapsed ? "Expandir menu" : "Recolher menu"}
              aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
            >
              {collapsed ? <ChevronRight className="w-6 h-6 shrink-0" /> : <ChevronLeft className="w-6 h-6 shrink-0" />}
              {!collapsed && <span>Recolher menu</span>}
            </button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className={`w-full ${collapsed ? "justify-center px-0" : "justify-start gap-3"} ${t.isDarkSidebar ? "text-white hover:bg-white/10" : ""}`}
              data-testid="desktop-logout"
              title={collapsed ? "Sair" : undefined}
            >
              <LogOut className="w-6 h-6 shrink-0" strokeWidth={2.4} />
              {!collapsed && "Sair"}
            </Button>
          </div>
        </aside>

        {/* Mobile sidebar drawer */}
        {mobileSidebarOpen && (
          <div className="md:hidden fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/60" onClick={() => setMobileSidebarOpen(false)} />
            <aside className={`absolute left-0 top-0 bottom-0 w-72 ${t.sidebar} ${t.sidebarText || "text-zinc-900"} flex flex-col`}>
              <div className={`p-4 border-b ${t.isDarkSidebar ? "border-white/10" : "border-zinc-200"} flex items-center justify-between`}>
                <span className="font-bold text-lg">{config.name} {config.role}</span>
                <button onClick={() => setMobileSidebarOpen(false)} className={`p-2 rounded-lg ${t.isDarkSidebar ? "hover:bg-white/10" : "hover:bg-zinc-100"}`}>
                  <X className="w-7 h-7" strokeWidth={2.5} />
                </button>
              </div>
              <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
                {config.nav.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.to);
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setMobileSidebarOpen(false)}
                      className={`flex items-center gap-3 px-3 py-3 rounded-lg ${
                        active
                          ? t.isDarkSidebar
                            ? "bg-white/10 font-semibold"
                            : "bg-zinc-100 font-semibold"
                          : t.isDarkSidebar
                          ? "hover:bg-white/5"
                          : "hover:bg-zinc-100"
                      }`}
                    >
                      <Icon className="w-7 h-7" strokeWidth={2.4} />
                      <span className="flex-1">{item.label}</span>
                      {item.chatBadge && unreadTotal > 0 && <ChatBadge count={unreadTotal} testId={`${item.testId}-chat-badge-mobile`} />}
                    </Link>
                  );
                })}
              </nav>
            </aside>
          </div>
        )}

        {/* Main content */}
        <main key={location.pathname} className="flex-1 min-w-0 pb-20 md:pb-0 animate-page-in">{children}</main>
      </div>

      {/* Mobile bottom nav */}
      <nav
        className={`md:hidden fixed bottom-0 left-0 right-0 ${t.headerBg} border-t ${t.isDarkMode ? "border-zinc-800" : ""} z-30`}
        data-testid={`bottom-nav-${config.key}`}
      >
        <div className="grid grid-cols-5 gap-0">
          {config.nav.slice(0, 5).map((item) => {
            const Icon = item.icon;
            const active = isActive(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`bottom-${item.testId}`}
                className={`flex flex-col items-center justify-center py-2 px-1 ${
                  active ? t.accentText : t.isDarkMode ? "text-zinc-500" : "text-zinc-500"
                }`}
              >
                <div className="relative">
                  <Icon className="w-7 h-7" strokeWidth={active ? 2.8 : 2.4} />
                  {item.chatBadge && unreadTotal > 0 && (
                    <ChatBadge count={unreadTotal} className="absolute -top-1.5 -right-2.5" testId={`${item.testId}-chat-badge-bottom`} />
                  )}
                </div>
                <span className="text-[11px] mt-0.5 font-medium">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
